import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBrief, useUpdateBriefSlice, useSaveAndRebuildBrief } from '@/lib/brief';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/brief/services')({
  staticData: { crumb: 'Usługi' },
  component: ServicesPage,
});

const CATEGORIES = [
  'preventive',
  'diagnostic',
  'surgery',
  'dental',
  'rehabilitation',
  'specialty',
  'emergency',
  'consultation',
  'grooming',
  'end_of_life',
  'inny',
] as const;

const ServiceSchema = z.object({
  slug: z.string().regex(/^[a-z0-9-]{3,40}$/u, 'Slug: małe litery + myślniki, 3-40 znaków'),
  name_pl: z.string().min(3, 'Min. 3 znaki').max(120),
  category: z.enum(CATEGORIES),
  short_description: z.string().min(20, 'Min. 20 znaków').max(400),
  offered: z.boolean(),
  price_from_pln: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => {
      if (v == null || v === '') return undefined;
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
    }),
  notes_for_llm: z.string().max(400).optional().or(z.literal('')),
});
type ServiceInput = z.infer<typeof ServiceSchema>;

interface StoredInlineService {
  slug: string;
  name_pl: string;
  category: string;
  short_description: string;
  // ... other fields kept opaque
  [key: string]: unknown;
}
interface StoredProcedure {
  service_slug: string;
  offered: boolean;
  price_from_pln?: number;
  performed_by?: string[];
  notes_for_llm?: string;
  [key: string]: unknown;
}

function ServicesPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const saveAndRebuild = useSaveAndRebuildBrief();

  const [inlineServices, setInlineServices] = React.useState<StoredInlineService[]>(
    () => (brief?.inline_services ?? []) as StoredInlineService[]
  );
  const [procedures, setProcedures] = React.useState<StoredProcedure[]>(
    () => (brief?.procedures ?? []) as StoredProcedure[]
  );
  const [editingSlug, setEditingSlug] = React.useState<string | null>(null);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (!dirty) {
      setInlineServices((brief?.inline_services ?? []) as StoredInlineService[]);
      setProcedures((brief?.procedures ?? []) as StoredProcedure[]);
    }
  }, [brief?.inline_services, brief?.procedures, dirty]);

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await update.mutateAsync({
        patch: {
          inline_services: inlineServices,
          procedures,
        },
      });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  const onSaveAndRebuild = async () => {
    setSavedStatus('idle');
    try {
      await saveAndRebuild.mutateAsync({
        patch: {
          inline_services: inlineServices,
          procedures,
        },
      });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  const upsertService = (next: StoredInlineService, proc: StoredProcedure) => {
    setInlineServices((prev) => {
      const idx = prev.findIndex((s) => s.slug === next.slug);
      if (idx === -1) return [...prev, next];
      return prev.map((s, i) => (i === idx ? next : s));
    });
    setProcedures((prev) => {
      const idx = prev.findIndex((p) => p.service_slug === proc.service_slug);
      if (idx === -1) return [...prev, proc];
      return prev.map((p, i) => (i === idx ? proc : p));
    });
    setDirty(true);
  };

  const removeService = (slug: string) => {
    setInlineServices((prev) => prev.filter((s) => s.slug !== slug));
    setProcedures((prev) => prev.filter((p) => p.service_slug !== slug));
    setDirty(true);
  };

  const editingService =
    editingSlug != null
      ? {
          inline: inlineServices.find((s) => s.slug === editingSlug),
          proc: procedures.find((p) => p.service_slug === editingSlug),
        }
      : null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-6"
    >
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Usługi</h1>
        <p className="mt-1 text-sm text-text-muted">
          Procedury i usługi oferowane przez klinikę. Każda usługa ma własną stronę „zabieg"
          na publicznej witrynie. Wyłącz opcję „Oferowane" aby ukryć usługę bez jej kasowania.
        </p>
      </header>

      <Button
        type="button"
        variant="secondary"
        onClick={() => {
          const slug = `usluga-${inlineServices.length + 1}`;
          upsertService(
            {
              slug,
              name_pl: 'Nowa usługa',
              category: 'consultation',
              short_description: 'Krótki opis usługi (min. 20 znaków).',
            },
            {
              service_slug: slug,
              offered: true,
            }
          );
          setEditingSlug(slug);
        }}
      >
        <Plus className="size-4" aria-hidden="true" /> Dodaj usługę
      </Button>

      <ul role="list" className="grid gap-3">
        {inlineServices.map((s) => {
          const p = procedures.find((proc) => proc.service_slug === s.slug);
          return (
            <li
              key={s.slug}
              className="flex items-start justify-between gap-3 rounded-[var(--radius-card)] bg-bg p-4 ring-1 ring-black/5"
            >
              <button
                type="button"
                className="flex-1 text-left"
                onClick={() => setEditingSlug(s.slug)}
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <p className="font-medium text-text">{s.name_pl}</p>
                  <span className="font-mono text-xs text-text-subtle">{s.slug}</span>
                  <Badge variant={p?.offered ? 'success' : 'outline'}>
                    {p?.offered ? 'Oferowane' : 'Wyłączone'}
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-text-muted line-clamp-2">{s.short_description}</p>
                {p?.price_from_pln ? (
                  <p className="mt-1 text-xs text-text-subtle">
                    od <span className="tabular-nums">{p.price_from_pln}</span> zł
                  </p>
                ) : null}
              </button>
              <button
                type="button"
                aria-label="Usuń usługę"
                className="p-2 text-text-subtle hover:text-error"
                onClick={() => removeService(s.slug)}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>

      {inlineServices.length === 0 ? (
        <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
          Brak usług. Dodaj przynajmniej jedną, aby uruchomić indeks usług na stronie publicznej.
        </p>
      ) : null}

      {editingService?.inline && editingService.proc ? (
        <ServiceEditor
          key={editingService.inline.slug}
          inline={editingService.inline}
          proc={editingService.proc}
          team={(brief?.team ?? []) as Array<{ id: string; full_name: string }>}
          onClose={() => setEditingSlug(null)}
          onSave={(nextInline, nextProc) => {
            upsertService(nextInline, nextProc);
            setEditingSlug(null);
          }}
        />
      ) : null}

      <SaveBar
        saving={update.isPending}
        dirty={dirty}
        status={savedStatus}
        rebuilding={saveAndRebuild.isPending}
        onSaveAndRebuild={onSaveAndRebuild}
        onCancel={() => {
          setInlineServices((brief?.inline_services ?? []) as StoredInlineService[]);
          setProcedures((brief?.procedures ?? []) as StoredProcedure[]);
          setDirty(false);
        }}
      />
    </form>
  );
}

function ServiceEditor({
  inline,
  proc,
  team,
  onClose,
  onSave,
}: {
  inline: StoredInlineService;
  proc: StoredProcedure;
  team: Array<{ id: string; full_name: string }>;
  onClose: () => void;
  onSave: (nextInline: StoredInlineService, nextProc: StoredProcedure) => void;
}) {
  const form = useForm<ServiceInput>({
    resolver: zodResolver(ServiceSchema),
    defaultValues: {
      slug: inline.slug,
      name_pl: inline.name_pl,
      category: (inline.category as ServiceInput['category']) ?? 'consultation',
      short_description: inline.short_description,
      offered: proc.offered ?? true,
      price_from_pln: proc.price_from_pln,
      notes_for_llm: proc.notes_for_llm ?? '',
    },
  });

  const [performedBy, setPerformedBy] = React.useState<string[]>(proc.performed_by ?? []);

  const submit = (data: ServiceInput) => {
    const nextInline: StoredInlineService = {
      ...inline,
      slug: data.slug,
      name_pl: data.name_pl,
      category: data.category,
      short_description: data.short_description,
    };
    const nextProc: StoredProcedure = {
      ...proc,
      service_slug: data.slug,
      offered: data.offered,
      price_from_pln: data.price_from_pln,
      performed_by: performedBy,
      notes_for_llm: data.notes_for_llm || undefined,
    };
    onSave(nextInline, nextProc);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edytuj usługę</DialogTitle>
          <DialogDescription>
            Definicja usługi widoczna w publicznym indeksie + per-procedurze.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(submit)} className="space-y-4" noValidate>
          <Field
            label="Nazwa usługi"
            htmlFor="name_pl"
            required
            error={form.formState.errors.name_pl?.message}
          >
            <Input id="name_pl" {...form.register('name_pl')} />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Slug (URL)"
              htmlFor="slug"
              required
              hint="np. sterylizacja-suczki"
              error={form.formState.errors.slug?.message}
            >
              <Input id="slug" className="font-mono text-xs" {...form.register('slug')} />
            </Field>
            <Field
              label="Kategoria"
              htmlFor="category"
              error={form.formState.errors.category?.message}
            >
              <Select id="category" {...form.register('category')}>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field
            label="Krótki opis"
            htmlFor="short_description"
            required
            hint="20-400 znaków"
            error={form.formState.errors.short_description?.message}
          >
            <Textarea
              id="short_description"
              rows={3}
              {...form.register('short_description')}
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Cena od (zł)"
              htmlFor="price_from_pln"
              hint="opcjonalne"
              error={form.formState.errors.price_from_pln?.message}
            >
              <Input
                id="price_from_pln"
                type="number"
                inputMode="numeric"
                min={0}
                {...form.register('price_from_pln')}
              />
            </Field>
            <Field label="Oferowane" htmlFor="offered">
              <label className="flex h-10 items-center gap-2 px-1 text-sm">
                <Switch
                  id="offered"
                  checked={form.watch('offered')}
                  onCheckedChange={(checked) =>
                    form.setValue('offered', checked, { shouldDirty: true })
                  }
                />
                <span>Pokaż na publicznej stronie</span>
              </label>
            </Field>
          </div>

          <Field
            label="Wykonywane przez"
            hint="zaznacz członków zespołu (opcjonalnie)"
          >
            {team.length === 0 ? (
              <p className="text-xs text-text-subtle">
                Najpierw dodaj członków zespołu w zakładce „Zespół".
              </p>
            ) : (
              <ul role="list" className="flex flex-wrap gap-1.5">
                {team.map((t) => {
                  const active = performedBy.includes(t.id);
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setPerformedBy((prev) =>
                            active ? prev.filter((id) => id !== t.id) : [...prev, t.id]
                          );
                        }}
                        className={cn(
                          'rounded-full px-3 py-1 text-xs font-medium ring-1 transition-colors',
                          active
                            ? 'bg-accent/10 text-accent ring-accent/20'
                            : 'bg-bg text-text-muted ring-border hover:bg-surface'
                        )}
                      >
                        {t.full_name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Field>

          <Field
            label="Notatka dla generatora (opcjonalnie)"
            htmlFor="notes_for_llm"
            hint="max 400 znaków, wpływa na ton wygenerowanej strony"
            error={form.formState.errors.notes_for_llm?.message}
          >
            <Textarea id="notes_for_llm" rows={3} {...form.register('notes_for_llm')} />
          </Field>

          <div className="mt-2 flex justify-end gap-3 border-t border-border pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>
              Anuluj
            </Button>
            <Button type="submit">Zapisz zmiany lokalnie</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
